import { BackAppBar } from "@/components/ui";
import useProfile from "@/hooks/useProfile";
import { CASE_MEMBER_TYPE, QUERY_KEYS } from "@/libraries/constant";
import { isAxiosError } from "@/libraries/helper";
import {
	getCaseLetters,
	getCaseMedicalNotes,
	getCasePatientVisits,
	getCaseQueryOptions,
	getLatestCaseInfo,
} from "@/queries";
import { CaseDetailSearchParamSchema, DataIdSchema } from "@/schemas";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Toolbar from "@mui/material/Toolbar";
import {
	onlineManager,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { Suspense, lazy, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

const CaseInfoLetters = lazy(() => import("../../-components/CaseInfoLetters"));
const CaseInfoExternalMembers = lazy(
	() => import("../../-components/CaseInfoExternalMembers"),
);
const CaseInfoInternalMembers = lazy(
	() => import("../../-components/CaseInfoInternalMembers"),
);
const CaseInfoMedicalNote = lazy(
	() => import("../../-components/CaseInfoMedicalNote"),
);
const CaseInfoPatientDetail = lazy(
	() => import("../../-components/CaseInfoPatientDetail"),
);
const CaseInfoPatientLocation = lazy(
	() => import("../../-components/CaseInfoPatientLocation"),
);
const CaseInfoPatientVisit = lazy(
	() => import("../../-components/CaseInfoPatientVisit"),
);
const CaseInfoPendingAcceptance = lazy(
	() => import("../../-components/CaseInfoPendingAcceptance"),
);
const CaseInfoSummary = lazy(() => import("../../-components/CaseInfoSummary"));
const CaseInfoTitle = lazy(() => import("../../-components/CaseInfoTitle"));

export const Route = createFileRoute("/dashboard/case/view/$id/detail")({
	validateSearch: zodSearchValidator(CaseDetailSearchParamSchema),
	params: {
		parse: (params) => ({
			id: DataIdSchema.parse(Number(params.id)),
		}),
		stringify: ({ id }) => ({ id: id.toString() }),
	},
	loader: async ({ context, params }) => {
		try {
			if (onlineManager.isOnline() && !context.isRestoring) {
				const caseItem = await context.queryClient.ensureQueryData(
					getCaseQueryOptions(params.id),
				);
				return [
					caseItem,
					context.queryClient.ensureInfiniteQueryData(
						getCasePatientVisits({ caseId: params.id }),
					),
					context.queryClient.ensureInfiniteQueryData(
						getCaseMedicalNotes(params.id),
					),
					context.queryClient.ensureInfiniteQueryData(
						getCaseLetters(params.id),
					),
					context.queryClient.ensureQueryData(
						getLatestCaseInfo({ caseId: params.id, info_type: "summaries" }),
					),
					context.queryClient.ensureQueryData(
						getLatestCaseInfo({ caseId: params.id, info_type: "allergies" }),
					),
					context.queryClient.ensureQueryData(
						getLatestCaseInfo({ caseId: params.id, info_type: "medication" }),
					),
				];
			}
			return [
				context.queryClient.getQueryData([QUERY_KEYS.GET_CASE, params.id]),
				context.queryClient.getQueryData([
					QUERY_KEYS.GET_CASE_PATIENT_VISITS,
					params.id,
				]),
				context.queryClient.getQueryData([
					QUERY_KEYS.GET_CASE_MEDICAL_NOTES,
					params.id,
				]),
				context.queryClient.getQueryData([
					QUERY_KEYS.GET_CASE_LETTERS,
					params.id,
				]),
				context.queryClient.getQueryData([
					QUERY_KEYS.GET_CASE_INFOS,
					{ caseId: params.id, info_type: "summaries", latest: true },
				]),
				context.queryClient.getQueryData([
					QUERY_KEYS.GET_CASE_INFOS,
					{ caseId: params.id, info_type: "allergies", latest: true },
				]),
				context.queryClient.getQueryData([
					QUERY_KEYS.GET_CASE_INFOS,
					{ caseId: params.id, info_type: "medication", latest: true },
				]),
			];
		} catch (error) {
			if (isAxiosError(error, 404)) {
				throw notFound();
			}
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const caseId = Route.useParams({
		select: (s) => s.id,
	});
	const tab = Route.useSearch({
		select: (s) => s.tab as CaseDetailSearchParamSchema["tab"],
	});
	const { profile } = useProfile();
	const caseQuery = useSuspenseQuery(getCaseQueryOptions(caseId));
	const caseItem = caseQuery.data;
	const medicalNotesQuery = useSuspenseInfiniteQuery(
		getCaseMedicalNotes(caseItem.id),
	);
	const { data: resCaseLetters } = useSuspenseInfiniteQuery(
		getCaseLetters(caseItem.id),
	);

	const currentUserRole = useMemo(() => {
		if (!profile) return "external" as const;
		if (caseItem.user.id === profile.id) return "owner";
		if (
			caseItem.member_type === CASE_MEMBER_TYPE.EXTERNAL_REQUEST_MEMBER ||
			caseItem.external_members.some((member) => member.id === profile.id)
		)
			return "external" as const;
		if (caseItem.internal_members.some((member) => member.id === profile.id))
			return "member" as const;
		return "member";
	}, [profile, caseItem]);

	const handleChange = useCallback(
		(
			e: React.SyntheticEvent<Element, Event>,
			value: CaseDetailSearchParamSchema["tab"],
		) => {
			e.preventDefault();
			return navigate({
				replace: true,
				search: {
					tab: value,
				},
			});
		},
		[navigate],
	);

	return (
		<>
			<BackAppBar
				className="!bg-gradient-account [&_*_]:text-white"
				title={t("caseRecord.title")}
				titleClassName="flex-1"
			>
				<div className="md:container">
					<Toolbar classes={{ root: "!p-0 !items-end !min-h-0" }}>
						<Tabs
							value={tab}
							onChange={handleChange}
							indicatorColor="primary"
							textColor="inherit"
							aria-label="full width tabs example"
							className="w-full [&_span.MuiTabs-indicator]:!bg-white"
							variant="scrollable"
							scrollButtons="auto"
							allowScrollButtonsMobile
						>
							<Tab
								label={t("caseRecord.tabs.patient")}
								value="patient"
								sx={{ textTransform: "none" }}
							/>
							<Tab
								label={t("caseRecord.tabs.medicalNotes", {
									count:
										medicalNotesQuery.data?.pages?.[0]?.meta?.total_count ?? 0,
								})}
								value="medical_notes"
								sx={{ textTransform: "none" }}
							/>
							<Tab
								label={t("caseRecord.tabs.caseSummary")}
								value="case_summary"
								sx={{ textTransform: "none" }}
							/>
							<Tab
								label={t("caseRecord.tabs.letters", {
									count: resCaseLetters?.pages?.[0]?.meta?.total_count ?? 0,
								})}
								value="letters"
								sx={{ textTransform: "none" }}
							/>
							<Tab
								label={t("caseRecord.tabs.members")}
								value="members"
								sx={{ textTransform: "none" }}
							/>
						</Tabs>
					</Toolbar>
				</div>
			</BackAppBar>
			<div className="relative flex-1 flex flex-col gap-2 bg-gray-light overflow-hidden">
				<Suspense>
					{tab === "patient" ? (
						<div className="overflow-y-auto">
							<CaseInfoTitle
								caseId={caseItem.id}
								title={caseItem.title}
								isActive={caseItem.status === "active"}
								currentUserRole={currentUserRole}
							/>
							<CaseInfoPatientDetail
								caseItem={caseItem}
								currentUserRole={currentUserRole}
							/>
							<CaseInfoPatientLocation
								caseItem={caseItem}
								currentUserRole={currentUserRole}
							/>
							<CaseInfoPatientVisit
								caseId={caseItem.id}
								isActive={caseItem.status === "active"}
								currentUserRole={currentUserRole}
							/>
						</div>
					) : null}
					{tab === "medical_notes" ? (
						<CaseInfoMedicalNote
							caseItem={caseItem}
							isActive={caseItem.status === "active"}
							currentUserRole={currentUserRole}
						/>
					) : null}
					{tab === "case_summary" ? (
						<CaseInfoSummary
							caseItem={caseItem}
							currentUserRole={currentUserRole}
						/>
					) : null}
					{tab === "letters" ? (
						<CaseInfoLetters
							caseItem={caseItem}
							currentUserRole={currentUserRole}
						/>
					) : null}
					{tab === "members" ? (
						<div className="overflow-y-auto">
							<CaseInfoInternalMembers
								profileId={profile?.id}
								caseItem={caseItem}
								currentUserRole={currentUserRole}
							/>
							<CaseInfoExternalMembers
								ownerId={caseItem.user.id}
								profileId={profile?.id}
								caseItem={caseItem}
								currentUserRole={currentUserRole}
							/>
							{currentUserRole === "owner" ? (
								<CaseInfoPendingAcceptance
									ownerId={caseItem.user.id}
									profileId={profile?.id}
									caseItem={caseItem}
									pendingMembers={caseItem.pending_members}
								/>
							) : null}
						</div>
					) : null}
				</Suspense>
			</div>
		</>
	);
}
